import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { Base } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/HistoryPanel/CollapsableItemDetails/Base'
import { useLeavers } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/components/HistoryPanel/hooks/useLeavers'

interface Props {
  employeeIds: number[]
  startDate: string
  endDate: string
}

export const Leavers = ({ employeeIds, startDate, endDate }: Props) => {
  const { fetch, data, loading } = useLeavers()

  const handleFetch = (itemsPerPage: number, lastCursor?: string) => {
    fetch({ employeeIds, startDate, endDate, itemsPerPage, lastCursor })
  }

  const connection = data?.employees.employeesConnection

  const formattedData = useUnsafeMemo(() => {
    return (
      connection?.edges
        .map((edge) => {
          const employee = edge.node

          // If the employee has no contract versions, we need to return the terminatedOn date from employee
          if (employee.contractversionsConnection.nodes.length === 0) {
            return [
              {
                id: String(employee.id),
                date: employee.terminatedOn ?? '',
                employeeName: employee.fullName,
                employeeId: employee.id,
              },
            ]
          }

          return employee.contractversionsConnection.nodes.map((contractVersion) => ({
            date: contractVersion.endsOn ?? employee.terminatedOn ?? '',
            contractVersionId: String(contractVersion.id),
            id: `${employee.id}-${contractVersion.id}`,
            employeeName: employee.fullName,
            employeeId: employee.id,
          }))
        })
        .flat() ?? []
    )
  }, [data])

  const formattedPagination = useUnsafeMemo(() => {
    return {
      hasNextPage: connection?.pageInfo.hasNextPage ?? false,
      endCursor: connection?.pageInfo.endCursor ?? '',
      totalCount: connection?.totalCount ?? 0,
    }
  }, [data])

  return (
    <Base
      fetch={handleFetch}
      loading={loading}
      count={employeeIds.length}
      data={formattedData}
      type="leavers"
      pagination={formattedPagination}
    />
  )
}
